import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { ClaimOffsets } from '../../../../models/api/view/claim/financials/offsets/claim-offsets';
import { EffectiveDeductions } from '../../../../models/api/view/claim/financials/offsets/effective-deductions';
import { Supplements } from '../../../../models/api/view/claim/financials/offsets/supplements';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { ClaimExportMapper } from '../../../../utilities/claim-export-mapper';
import { ExportService } from '../../../../services/export/export.service';
import { DatePipe } from '@angular/common';
import { CompanionAdjustments } from '../../../../models/api/view/claim/financials/offsets/companion-adjustments';
import { Increases } from '../../../../models/api/view/claim/financials/offsets/increases';

@Component({
  selector: 'app-offsets',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    CardComponent,
  ],
  template: `
    <app-page-header label="Offsets" />

    <div class="tw-flex tw-flex-col tw-pb-8 tw-gap-8">
      @if (!au && !gl && !pr) {
        <app-card label="claim offsets">
          <div>
            <app-table
              [dataSource]="claimOffsets"
              [pageSize]="10"
              [scrollable]="false"
              dataKey="id"
              recordLabel="Claim offset"
              [showExport]="true"
              (export)="onExportClaimOffsets($event)"
            >
              <app-column name="type" label="Type">
                <ng-template let-data>
                  <span class="tw-font-bold">{{ data.type }}</span>
                </ng-template>
              </app-column>
              <app-column name="subType" label="SubType" />
              <app-column name="amount" label="Amount" format="c" />
              <app-column name="frequency" label="Frequency" />
              <app-column name="inception" label="Inception" format="d" />
              <app-column name="expiration" label="Expiration" format="d" />
            </app-table>
          </div>
        </app-card>
      }
      @if (disability || au || gl || pr) {
        <app-card label="Elective deductions">
          <div>
            <app-table
              [dataSource]="effectiveDeductions"
              [pageSize]="5"
              [scrollable]="false"
              dataKey="id"
              recordLabel="Elective deduction"
              [showExport]="true"
              (export)="onExportDeductions($event)"
              [sortOrder]="1"
              sortField="type"
            >
              <app-column name="type" label="Type">
                <ng-template let-data>
                  <span class="tw-font-bold">{{ data.type }}</span>
                </ng-template>
              </app-column>
              <app-column name="inPercentage" label="In percentage" />
              <app-column name="amount" label="Amount" format="c" />
              <app-column name="frequency" label="Frequency" />
              <app-column name="inception" label="Inception" format="d" />
              <app-column name="expiration" label="Expiration" format="d" />
            </app-table>
          </div>
        </app-card>
      }
      @if (!au && !gl && !pr) {
        <app-card label="supplements">
          <div>
            <app-table
              [dataSource]="supplements"
              [pageSize]="5"
              [scrollable]="false"
              dataKey="id"
              recordLabel="Supplement"
              [showExport]="true"
              (export)="onExportSupplements($event)"
            >
              <app-column name="type" label="Type">
                <ng-template let-data>
                  <span class="tw-font-bold">{{ data.type }}</span>
                </ng-template>
              </app-column>
              <app-column name="subType" label="SubType" />
              <app-column name="amount" label="Amount" format="c" />
              <app-column name="frequency" label="Frequency" />
              <app-column name="inception" label="Inception" format="d" />
              <app-column name="expiration" label="Expiration" format="d" />
              <app-column name="pretax" label="Pretax" format="b" />
            </app-table>
          </div>
        </app-card>
        @if (workersComp) {
          <app-card label="Increases">
            <div>
              <app-table
                [dataSource]="increases"
                [pageSize]="5"
                [scrollable]="false"
                recordLabel="increase"
                [showExport]="true"
                (export)="onExportIncreases($event)"
              >
                <app-column name="type" label="Type">
                  <ng-template let-data>
                    <span class="tw-font-bold">{{ data.type }}</span>
                  </ng-template>
                </app-column>
                <app-column name="subType" label="SubType" />
                <app-column name="amount" label="Amount" format="c" />
                <app-column name="frequency" label="Frequency" />
                <app-column name="inception" label="Inception" format="d" />
                <app-column name="expiration" label="Expiration" format="d" />
              </app-table>
            </div>
          </app-card>
          <app-page-header label="Companion Adjustments" />
          <app-card label="Companion adjustments" class="tw--mt-8">
            <div>
              <app-table
                [dataSource]="companionAdjustments"
                [pageSize]="5"
                [scrollable]="false"
                recordLabel="adjustment"
              >
                <app-column name="amount" label="Amount" format="c" />
                <app-column name="frequency" label="Frequency" />
                <app-column name="inception" label="Inception" format="d" />
                <app-column name="expiration" label="Expiration" format="d" />
                <app-column
                  name="reduceFromCompRate"
                  label="Reduce from comp rate"
                  format="b"
                />
                <app-column name="payCode" label="Pay code" />
                <app-column name="docType" label="Document type" />
              </app-table>
            </div>
          </app-card>
        }
      }
    </div>
  `,
  styles: ``,
})
export class OffsetsComponent extends BaseComponent implements OnInit {
  exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  disability = false;
  workersComp = false;
  au = false;
  gl = false;
  pr = false;

  sups: any[] = [];

  comps: any[] = [];

  claim = this.view.currentClaim();

  claimOffsets: ClaimOffsets[] = [];
  effectiveDeductions: EffectiveDeductions[] = [];
  supplements: Supplements[] = [];
  companionAdjustments: CompanionAdjustments[] = [];
  increases: Increases[] = [];

  async ngOnInit() {
    this.disability = this.claim.lineOfBusiness === 'DS';
    this.workersComp = this.claim.lineOfBusiness === 'WC';
    this.au = this.claim.lineOfBusiness === 'AU';
    this.gl = this.claim.lineOfBusiness === 'GL';
    this.pr = this.claim.lineOfBusiness === 'PR';
    const claimKey: ClaimKey = {
      recordId: this.claim.recordId,
      recordSource: this.claim.recordSource,
    };
    const offsetsResponse = await this.api.offsets.get(claimKey);
    this.claimOffsets = offsetsResponse.claimOffsets;
    this.effectiveDeductions = offsetsResponse.effectiveDeductions;
    this.supplements = offsetsResponse.supplements;
    this.companionAdjustments = offsetsResponse.companionAdjustments;
    this.increases = offsetsResponse.increases;
    this.view.hideLoading();
  }

  async onExportClaimOffsets(format: ExportFormat) {
    const columns = [
      { propertyName: 'type', headerText: 'Type' },
      { propertyName: 'subType', headerText: 'Subtype' },
      { propertyName: 'amount', headerText: 'Amount', format: 'currency' },
      { propertyName: 'frequency', headerText: 'Frequency' },
      { propertyName: 'inception', headerText: 'Inception', format: 'date' },
      { propertyName: 'expiration', headerText: 'Expiration', format: 'date' },
    ];

    const rows = this.claimOffsets;
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const fileName = `ClaimOffsets_${formattedDate}.${format}`;
    const sheetLabel = 'Claim Offsets';

    await this.onExport(format, columns, rows, fileName, sheetLabel);
  }

  async onExportDeductions(format: ExportFormat) {
    const columns = [
      { propertyName: 'type', headerText: 'Type' },
      { propertyName: 'inPercentage', headerText: 'In Percentage' },
      { propertyName: 'amount', headerText: 'Amount', format: 'currency' },
      { propertyName: 'frequency', headerText: 'Frequency' },
      { propertyName: 'inception', headerText: 'Inception', format: 'date' },
      { propertyName: 'expiration', headerText: 'Expiration', format: 'date' },
    ];

    const rows = this.effectiveDeductions;
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const fileName = `ElectiveDeductions_${formattedDate}.${format}`;
    const sheetLabel = 'Elective Deductions';

    await this.onExport(format, columns, rows, fileName, sheetLabel);
  }

  async onExportSupplements(format: ExportFormat) {
    const columns = [
      { propertyName: 'type', headerText: 'Type' },
      { propertyName: 'subType', headerText: 'Subtype' },
      { propertyName: 'amount', headerText: 'Amount', format: 'currency' },
      { propertyName: 'frequency', headerText: 'Frequency' },
      { propertyName: 'inception', headerText: 'Inception', format: 'date' },
      { propertyName: 'expiration', headerText: 'Expiration', format: 'date' },
      { propertyName: 'pretax', headerText: 'Pretax' },
    ];

    const rows = this.supplements.map((supplement) => ({
      ...supplement,
      pretax: supplement.pretax ? 'Yes' : 'No',
    }));
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const fileName = `Supplements_${formattedDate}.${format}`;
    const sheetLabel = 'Supplements';

    await this.onExport(format, columns, rows, fileName, sheetLabel);
  }

  async onExportIncreases(format: ExportFormat) {
    const columns = [
      { propertyName: 'type', headerText: 'Type' },
      { propertyName: 'subType', headerText: 'Subtype' },
      { propertyName: 'amount', headerText: 'Amount', format: 'currency' },
      { propertyName: 'frequency', headerText: 'Frequency' },
      { propertyName: 'inception', headerText: 'Inception', format: 'date' },
      { propertyName: 'expiration', headerText: 'Expiration', format: 'date' },
    ];

    const rows = this.increases;
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const fileName = `Increases_${formattedDate}.${format}`;
    const sheetLabel = 'Increases';

    await this.onExport(format, columns, rows, fileName, sheetLabel);
  }

  async onExport(
    format: ExportFormat,
    columns: any[],
    rows: any[],
    fileName: string,
    sheetLabel: string,
  ) {
    if (format === 'csv') {
      const csvData = ClaimExportMapper.toCsvExport(rows, columns, sheetLabel);
      await this.exportService.exportCsv(csvData, fileName);
    } else if (format === 'xlsx') {
      const excelData = ClaimExportMapper.toExcelExport(
        rows,
        columns,
        sheetLabel,
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, fileName);
    }
  }
}
